import { useState, useEffect } from 'react';
import { X, DeviceMobile, ShareNetwork, Plus } from '@phosphor-icons/react';

let deferredPrompt = null;

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptType, setPromptType] = useState(null); // 'native' | 'ios'

  useEffect(() => {
    if (isInStandaloneMode()) return;

    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedAt = dismissed ? parseInt(dismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    if (dismissed && daysSinceDismissed < 7) return;

    // Android/Desktop: capture beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setTimeout(() => {
        setPromptType('native');
        setShowPrompt(true);
      }, 30000);
    };

    const handleInstalled = () => {
      setShowPrompt(false);
      deferredPrompt = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    // iOS Safari: show manual instructions after 30s
    if (isIOS()) {
      setTimeout(() => {
        setPromptType('ios');
        setShowPrompt(true);
      }, 30000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'dismissed') {
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }
    deferredPrompt = null;
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  // iOS-specific instructions
  if (promptType === 'ios') {
    return (
      <div
        className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-50 animate-in slide-in-from-bottom-5 duration-500"
        data-testid="pwa-install-prompt-ios"
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 bg-gradient-to-br from-[#781C31] to-[#A0344D] rounded-xl flex items-center justify-center">
              <DeviceMobile size={20} className="text-white" weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-gray-900">Install Winery Finder</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                Add to your home screen for offline tours & a full-screen experience
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="shrink-0 text-gray-300 hover:text-gray-500 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* iOS step-by-step */}
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2.5">
            <div className="flex items-center gap-2.5">
              <div className="shrink-0 w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center">
                <ShareNetwork size={14} className="text-blue-600" weight="bold" />
              </div>
              <p className="text-xs text-gray-600">
                Tap the <span className="font-semibold">Share</span> button in Safari
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="shrink-0 w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center">
                <Plus size={14} className="text-blue-600" weight="bold" />
              </div>
              <p className="text-xs text-gray-600">
                Select <span className="font-semibold">"Add to Home Screen"</span>
              </p>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="w-full mt-3 text-gray-400 hover:text-gray-600 text-xs py-1.5 transition-colors"
            data-testid="pwa-dismiss-btn"
          >
            Not now
          </button>
        </div>
      </div>
    );
  }

  // Android/Desktop native install
  return (
    <div
      className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-50 animate-in slide-in-from-bottom-5 duration-500"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 bg-gradient-to-br from-[#781C31] to-[#A0344D] rounded-xl flex items-center justify-center">
          <DeviceMobile size={20} className="text-white" weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900">Install Winery Finder</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            Access tours offline & get a faster experience
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="bg-[#781C31] hover:bg-[#5a1525] text-white text-xs font-medium px-4 py-1.5 rounded-full transition-colors"
              data-testid="pwa-install-btn"
            >
              Install
            </button>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 text-xs px-2 py-1.5 transition-colors"
              data-testid="pwa-dismiss-btn"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="shrink-0 text-gray-300 hover:text-gray-500 transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
